import React, { useState, useEffect } from 'react';
import { WASTE_TYPES, SKIP_SIZES, JOB_TYPES, JOB_STATUSES, PAYMENT_STATUSES } from '../constants/jobOptions';

// UK postcode regex pattern - matches formats like: M1 1AA, SW1A 1AA, B33 8TH, BB4 5AN, BB45AN
const UK_POSTCODE_REGEX = /^([A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2})$/i;

// Helper function to validate UK postcode
const isValidUkPostcode = (postcode) => {
    if (!postcode || !postcode.trim()) return true; // Empty is valid (optional field)
    return UK_POSTCODE_REGEX.test(postcode.trim());
};

// Helper function to format UK postcode with proper spacing
const formatUkPostcode = (postcode) => {
    if (!postcode) return '';
    const cleaned = postcode.replace(/\s/g, '').toUpperCase();
    if (cleaned.length >= 5) {
        // Insert space before last 3 characters
        return `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`;
    }
    return cleaned;
};

// Helper function to extract postcode from address string (for legacy records)
const extractPostcodeFromAddress = (address) => {
    if (!address) return '';
    const match = address.match(/[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}/i);
    return match ? formatUkPostcode(match[0]) : '';
};

const EditModal = ({ isOpen, onClose, onSave, job, isProcessing = false, mode = 'edit' }) => {
    const [formData, setFormData] = useState({
        streetAddress: '',
        postcode: '',
        businessName: '',
        size: '',
        wasteType: '',
        jobType: '',
        status: '',
        driverNote: '',
        paymentStatus: 'Unknown',
        invoiced: false
    });
    const [newImage, setNewImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (mode === 'add') {
                // Reset form for adding new record
                setFormData({
                    streetAddress: '',
                    postcode: '',
                    businessName: '',
                    size: '',
                    wasteType: '',
                    jobType: '',
                    status: 'delivered',
                    driverNote: '',
                    paymentStatus: 'Unknown',
                    invoiced: false
                });
                setImagePreview(null);
                setNewImage(null);
                setErrors({});
            } else if (job) {
                // Load existing job data for editing
                const fullAddress = job.formattedAddress || job.streetAddress || '';
                const detectedPostcode = job.postcode || extractPostcodeFromAddress(fullAddress);

                // Strip postcode from street address to prevent duplication on save
                // (formattedAddress includes postcode, e.g. "12 High St, BB4 5AN")
                let cleanStreetAddress = fullAddress;
                if (detectedPostcode) {
                    const escaped = detectedPostcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s*');
                    // Remove one or more trailing ", POSTCODE" suffixes (handles prior duplication)
                    cleanStreetAddress = fullAddress.replace(new RegExp('(\\s*,\\s*' + escaped + ')+\\s*$', 'i'), '').trim();
                }

                setFormData({
                    streetAddress: cleanStreetAddress,
                    postcode: detectedPostcode ? formatUkPostcode(detectedPostcode) : '',  // Fallback: extract from address for legacy records
                    businessName: job.businessName || '',  // Load business name if available
                    size: job.size || '',
                    wasteType: job.wasteType || job.description || '',
                    jobType: job.jobType || job.name || '',
                    status: job.status || '',
                    driverNote: job.driverNote || '',
                    paymentStatus: job.paymentStatus || 'Unknown',
                    invoiced: job.invoiced === true
                });
                setImagePreview(job.imageUrl || null);
                setNewImage(null);
                setErrors({});
            }
        }
    }, [job, isOpen, mode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setErrors(prev => ({ ...prev, image: 'Please select a valid image file' }));
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB' }));
                return;
            }

            setNewImage(file);
            setErrors(prev => ({ ...prev, image: '' }));

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.streetAddress.trim()) {
            newErrors.streetAddress = 'Street address is required';
        }
        if (formData.postcode.trim() && !isValidUkPostcode(formData.postcode)) {
            newErrors.postcode = 'Invalid UK postcode format';
        }
        if (!formData.size) {
            newErrors.size = 'Size is required';
        }
        if (!formData.wasteType) {
            newErrors.wasteType = 'Waste type is required';
        }
        if (!formData.jobType) {
            newErrors.jobType = 'Job type is required';
        }
        if (!formData.status) {
            newErrors.status = 'Status is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }

        // Format postcode if provided
        const normalizedPostcode = formData.postcode.trim()
            ? formatUkPostcode(formData.postcode)
            : '';

        // Build formatted address (street + postcode if available)
        const formattedAddress = normalizedPostcode
            ? `${formData.streetAddress.trim()}, ${normalizedPostcode}`
            : formData.streetAddress.trim();

        onSave({
            ...formData,
            streetAddress: formData.streetAddress.trim(),
            postcode: normalizedPostcode,
            businessName: formData.businessName.trim(),
            formattedAddress: formattedAddress,
            newImage: newImage
        });
    };

    const handleClose = () => {
        setFormData({
            streetAddress: '',
            postcode: '',
            businessName: '',
            size: '',
            wasteType: '',
            jobType: '',
            status: '',
            driverNote: '',
            paymentStatus: 'Unknown',
            invoiced: false
        });
        setNewImage(null);
        setImagePreview(null);
        setErrors({});
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            overflow: 'auto'
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', padding: '24px 24px 16px', margin: 0, color: '#374151', flexShrink: 0 }}>
                    {mode === 'add' ? 'Add New Skip Record' : 'Edit Skip Record'}
                </h2>

                {/* Scrollable form content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '0 24px' }}>

                {/* Street Address */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Street Address *
                    </label>
                    <input
                        type="text"
                        name="streetAddress"
                        value={formData.streetAddress}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: `1px solid ${errors.streetAddress ? '#EF4444' : '#D1D5DB'}`,
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                        placeholder="e.g. 12 High Street, Manchester"
                    />
                    {errors.streetAddress && (
                        <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                            {errors.streetAddress}
                        </p>
                    )}
                </div>

                {/* Postcode */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Postcode
                    </label>
                    <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={(e) => handleChange({
                            target: {
                                name: 'postcode',
                                value: e.target.value.toUpperCase()
                            }
                        })}
                        style={{
                            width: '100%',
                            maxWidth: '200px',
                            padding: '10px',
                            border: `1px solid ${errors.postcode ? '#EF4444' : '#D1D5DB'}`,
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                        placeholder="e.g. M1 1AA"
                    />
                    {errors.postcode && (
                        <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                            {errors.postcode}
                        </p>
                    )}
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        Optional. UK postcode format.
                    </p>
                </div>

                {/* Business Name */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Business Name
                    </label>
                    <input
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                        placeholder="e.g. Warburton Bakery"
                    />
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        Optional. Add if delivering to a business premises.
                    </p>
                </div>

                {/* Size */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Size *
                    </label>
                    <select
                        name="size"
                        value={formData.size}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: `1px solid ${errors.size ? '#EF4444' : '#D1D5DB'}`,
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    >
                        <option value="">Select size</option>
                        {SKIP_SIZES.map((size, index) => (
                            <option key={index} value={size}>{size}</option>
                        ))}
                    </select>
                    {errors.size && (
                        <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                            {errors.size}
                        </p>
                    )}
                </div>

                {/* Waste Type */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Waste Type *
                    </label>
                    <select
                        name="wasteType"
                        value={formData.wasteType}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: `1px solid ${errors.wasteType ? '#EF4444' : '#D1D5DB'}`,
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    >
                        <option value="">Select waste type</option>
                        {WASTE_TYPES.map((type, index) => (
                            <option key={index} value={type}>{type}</option>
                        ))}
                    </select>
                    {errors.wasteType && (
                        <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                            {errors.wasteType}
                        </p>
                    )}
                </div>

                {/* Job Type */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Job Type *
                    </label>
                    <select
                        name="jobType"
                        value={formData.jobType}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: `1px solid ${errors.jobType ? '#EF4444' : '#D1D5DB'}`,
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    >
                        <option value="">Select job type</option>
                        {JOB_TYPES.map((type, index) => (
                            <option key={index} value={type}>{type}</option>
                        ))}
                    </select>
                    {errors.jobType && (
                        <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                            {errors.jobType}
                        </p>
                    )}
                </div>

                {/* Status */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Status *
                    </label>
                    <select
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: `1px solid ${errors.status ? '#EF4444' : '#D1D5DB'}`,
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    >
                        <option value="">Select status</option>
                        {JOB_STATUSES.map((status, index) => (
                            <option key={index} value={status}>{status}</option>
                        ))}
                    </select>
                    {errors.status && (
                        <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                            {errors.status}
                        </p>
                    )}
                </div>

                {/* Payment Status */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Payment Status
                    </label>
                    <select
                        name="paymentStatus"
                        value={formData.paymentStatus}
                        onChange={handleChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    >
                        {PAYMENT_STATUSES.map((status, index) => (
                            <option key={index} value={status}>{status}</option>
                        ))}
                    </select>
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        Payment status for this job.
                    </p>
                </div>

                {/* Invoiced */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        cursor: 'pointer'
                    }}>
                        <input
                            type="checkbox"
                            checked={formData.invoiced}
                            onChange={(e) => setFormData(prev => ({ ...prev, invoiced: e.target.checked }))}
                            style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer'
                            }}
                        />
                        Invoiced
                    </label>
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        Mark if an invoice has been sent for this job.
                    </p>
                </div>

                {/* Note */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Note
                    </label>
                    <textarea
                        name="driverNote"
                        value={formData.driverNote}
                        onChange={handleChange}
                        maxLength={250}
                        rows="4"
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                        }}
                        placeholder="Add any relevant notes (e.g., Gate code: 1234, Contact: John 555-1234, Don't visit at 3pm...)"
                    />
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        {formData.driverNote.length}/250 characters
                    </p>
                </div>

                {/* Image */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151'
                    }}>
                        Image
                    </label>

                    {imagePreview && (
                        <div style={{
                            marginBottom: '12px',
                            maxWidth: '300px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            overflow: 'hidden'
                        }}>
                            <img
                                src={imagePreview}
                                alt="Preview"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block'
                                }}
                            />
                        </div>
                    )}

                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        style={{
                            width: '100%',
                            padding: '10px',
                            border: `1px solid ${errors.image ? '#EF4444' : '#D1D5DB'}`,
                            borderRadius: '4px',
                            fontSize: '14px'
                        }}
                    />
                    {errors.image && (
                        <p style={{ color: '#EF4444', fontSize: '12px', marginTop: '4px' }}>
                            {errors.image}
                        </p>
                    )}
                    <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                        {mode === 'add'
                            ? (newImage ? 'Image selected for upload.' : 'Optional: Add an image for this job.')
                            : (newImage ? 'New image selected. Old image will be replaced.' : 'Leave empty to keep current image.')
                        }
                    </p>
                </div>

                </div>{/* End scrollable form content */}

                {/* Sticky Buttons */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', padding: '16px 24px', borderTop: '1px solid #E5E7EB', flexShrink: 0, backgroundColor: 'white' }}>
                    <button
                        onClick={handleClose}
                        disabled={isProcessing}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: 'white',
                            border: '1px solid #D1D5DB',
                            borderRadius: '4px',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isProcessing}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: isProcessing ? '#9CA3AF' : '#10B981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isProcessing ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        {isProcessing ? 'Saving...' : (mode === 'add' ? 'Add Record' : 'Save Changes')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditModal;
